import { inject, Injectable } from '@angular/core';
import { Licenses, StartPageVersions } from '@shared/constants';
import { combineLatest, map, Observable } from 'rxjs';
import { NavItem } from '../shared/types';
import { AuthService } from './auth.service';
import { ConfigService } from './config.service';
import { LicensesService } from './licenses.service';

export type SideNavItems = {
    top: NavItem[];
    bottom: NavItem[];
};

@Injectable({ providedIn: 'root' })
export class SidenavService {
    public readonly sidenavItemsObs: Observable<SideNavItems>;
    private authService = inject(AuthService);

    constructor(
        private readonly _licenseService: LicensesService,
        private readonly _configService: ConfigService,
    ) {
        this.sidenavItemsObs = combineLatest([
            this._licenseService.licensesObs,
            this._configService.configObs,
        ]).pipe(
            map(([licenses, config]): SideNavItems => {
                const top: NavItem[] = [];

                if (config?.portal?.startPageVersion !== StartPageVersions.None) {
                    top.push(
                        {
                            type: 'router',
                            key: 'home',
                            icon: { name: 'home', fontSet: 'material-symbols-rounded' },
                            label: 'Startseite',
                            routerLink: '/start',
                        },
                        { type: 'separator', key: 'sep-top-2' },
                    );
                }

                const bottom: NavItem[] = [
                    {
                        type: 'router',
                        key: 'info',
                        icon: { name: 'info', fontSet: 'material-symbols-rounded' },
                        label: 'Informationen',
                        routerLink: '/info',
                    },
                ];

                if (config?.portal?.logoutButtonEnabled) {
                    bottom.push({
                        type: 'action',
                        key: 'logout',
                        icon: { name: 'logout', fontSet: 'material-symbols-rounded' },
                        label: 'Abmelden',
                        action: () => this.authService.logoutSso(),
                    });
                }

                if (licenses?.has(Licenses.PatientsSearch)) {
                    top.push({
                        type: 'router',
                        key: 'patients-search',
                        icon: { name: 'person_search', fontSet: 'material-symbols-rounded' },
                        label: 'Patientensuche',
                        routerLink: '/patients-search',
                    });
                }

                if (licenses?.has(Licenses.OrderCreation)) {
                    top.push({
                        type: 'router',
                        key: 'order-creation',
                        icon: { name: 'assignment_add', fontSet: 'material-symbols-rounded' },
                        label: 'Auftragserstellung',
                        routerLink: '/order-creation',
                    });
                }

                if (licenses?.has(Licenses.Orders)) {
                    top.push({
                        type: 'router',
                        key: 'orders',
                        icon: { name: 'assignment', fontSet: 'material-symbols-rounded' },
                        label: 'Aufträge',
                        routerLink: '/orders',
                    });
                }

                if (licenses?.has(Licenses.Reports)) {
                    top.push({
                        type: 'router',
                        key: 'reports',
                        icon: { name: 'assignment_turned_in', fontSet: 'material-symbols-rounded' },
                        label: 'Befunde',
                        routerLink: '/reports',
                    });
                }

                if (licenses?.has(Licenses.Documents)) {
                    top.push({
                        type: 'router',
                        key: 'documents',
                        icon: { name: 'folder', fontSet: 'material-symbols-rounded' },
                        label: 'Dokumente',
                        routerLink: '/view-documents',
                    });
                }

                if (licenses?.has(Licenses.Shop)) {
                    top.push({
                        type: 'router',
                        key: 'shop',
                        icon: { name: 'shopping_cart', fontSet: 'material-symbols-rounded' },
                        label: 'Shopbereich',
                        routerLink: '/shop',
                    });
                }

                return {
                    top,
                    bottom,
                };
            }),
        );
    }
}
